/**
 * Locality vs IP-geo ranking for transactional event search (Algolia and Typesense).
 *
 * First request with a location uses optional locality filters. If that returns
 * zero hits while filters already match route defaults, a follow-up search sets
 * `isGeoFallbackAfterEmptyLocalityHits` so ranking uses IP geo instead.
 */

import {
  TYPESENSE_GEO_FIELD,
  buildTypesenseGeoSort,
  type GeoCoords,
} from '~/utils/typesenseGeo'
import { buildEventsRelevanceSort } from '~/utils/typesenseEventsTextSearch'

export interface LocalityRankingInput {
  hasLocationOptionalFilter: boolean
  onlineFilterActive: boolean
  /**
   * True after a strict locality search returned no hits — use IP geo ranking
   * instead of locality `optionalFilters`.
   */
  isGeoFallbackAfterEmptyLocalityHits: boolean
  useRegionsOptionalFilter: boolean
  slugForOptionalFilter: string
  /** Mirrors client-only Algolia IP geo (`import.meta.client` in components). */
  enableAroundLatLngViaIP: boolean
  /**
   * Viewer coordinates for the Typesense path (KFT-4116) — resolved client-side
   * via `useUserGeo()`. `null` on SSR or when geo is unavailable, so the geo
   * branch stays a no-op without them. Unused by the Algolia path (which geo-
   * biases via `aroundLatLngViaIP`).
   */
  coords?: GeoCoords | null
  /**
   * Whether a text query is active (KFT-4145). Without one every text score
   * ties, so the nearby-first relevance sort breaks ties by recent bookings
   * instead of text match.
   */
  hasQuery?: boolean
}

export type AlgoliaLocalityRankingRawOptions =
  | { optionalFilters: string[] }
  | {
      aroundLatLngViaIP: boolean
      aroundRadius: 'all'
      aroundPrecision: 15000
    }

export const getAlgoliaLocalityRankingRawOptions = (
  input: LocalityRankingInput
): AlgoliaLocalityRankingRawOptions => {
  if (
    input.hasLocationOptionalFilter &&
    !input.onlineFilterActive &&
    !input.isGeoFallbackAfterEmptyLocalityHits
  ) {
    return {
      optionalFilters: [
        input.useRegionsOptionalFilter
          ? `regions: ${input.slugForOptionalFilter}`
          : `city_slug: ${input.slugForOptionalFilter}`,
      ],
    }
  }
  return {
    aroundLatLngViaIP: input.enableAroundLatLngViaIP,
    aroundRadius: 'all',
    aroundPrecision: 15000,
  }
}

export type TypesenseLocalityRankingParams =
  | { sort_by: string }
  /** No locality boost and no coordinates — fall back to relevancy rather than
   * faking a geo sort. */
  | Record<string, never>

/**
 * Typesense equivalent of getAlgoliaLocalityRankingRawOptions (KFT-4028).
 * Locality boost works via _eval(); the geo-fallback branch (no city / online /
 * empty-locality retry) distance-sorts on the viewer's coordinates when present
 * (KFT-4116, replacing Algolia's aroundLatLngViaIP), else stays a no-op.
 */
export const getTypesenseLocalityRankingParams = (
  input: LocalityRankingInput
): TypesenseLocalityRankingParams => {
  if (
    input.hasLocationOptionalFilter &&
    !input.onlineFilterActive &&
    !input.isGeoFallbackAfterEmptyLocalityHits
  ) {
    const field = input.useRegionsOptionalFilter ? 'regions' : 'city_slug'
    return {
      sort_by: `_eval(${field}:=${input.slugForOptionalFilter}):desc`,
    }
  }
  const geoSort = buildTypesenseGeoSort(input.coords)
  return geoSort ? { sort_by: geoSort } : {}
}

// Tiebreaker after the city-tiering eval. No has_promotions: ranking sale
// events first within each tier pushed them above better matches on city
// pages. trending is a nested field on the schema — trending.7_days, not a
// flat trending_7_days. 7-day window, like EVENTS_RANKING.
const RELEVANCY_TIEBREAKERS = 'trending.7_days:desc'

/**
 * sort_by for the "relevancy" order option (KFT-4028 follow-up). Tiers
 * results by city match + upcoming availability, then breaks ties by
 * trending. With no city to tier by (no location, online
 * filter active, or mid geo-fallback) it ranks by distance or text match, then
 * has_events + trending.
 */
export const getTypesenseRelevancySortBy = (
  input: LocalityRankingInput
): string => {
  if (
    input.hasLocationOptionalFilter &&
    !input.onlineFilterActive &&
    !input.isGeoFallbackAfterEmptyLocalityHits
  ) {
    const field = input.useRegionsOptionalFilter ? 'regions' : 'city_slug'
    const slug = input.slugForOptionalFilter
    return `_eval([(${field}:=${slug} && has_events:true):4, (${field}:=${slug}):3, (has_events:true):2]):desc,${RELEVANCY_TIEBREAKERS}`
  }
  // No city to tier by — this is the branch Algolia geo-biased via
  // aroundLatLngViaIP. The same relevance sort the federated search uses
  // (KFT-4145), so a query ranks identically in the dropdown and here:
  // nearby/online/rest groups with relevance kept, or text-first without
  // coordinates.
  return buildEventsRelevanceSort(input.coords, Boolean(input.hasQuery))
}

export interface OrderByTypesenseOption {
  slug?: string
  typesenseSortBy?: string
  /** Matching preset created via scripts/create-typesense-sort-presets.mjs. */
  typesensePreset?: string
}

/**
 * sort_by/preset for the current order-by option (KFT-4023 presets
 * follow-up). Relevancy always resolves to an explicit sort_by (its primary
 * key is per-request, not a fixed string a preset could hold — see
 * getTypesenseRelevancySortBy). newest/price-asc hand off to their preset
 * when there's no locality boost to prepend; a preset's sort_by is fixed, and
 * an explicit sort_by on the same request replaces it rather than merging, so
 * boosted requests keep building sort_by explicitly like before.
 */
export const getTypesenseSortParams = (
  currentOrderBy: OrderByTypesenseOption | undefined,
  localityRankingInput: LocalityRankingInput
): { sortBy?: string; preset?: string } => {
  if (currentOrderBy?.slug === 'relevancy') {
    return { sortBy: getTypesenseRelevancySortBy(localityRankingInput) }
  }
  if (!currentOrderBy?.typesenseSortBy) {
    return {}
  }
  const localityParams = getTypesenseLocalityRankingParams(localityRankingInput)
  const boost = 'sort_by' in localityParams ? localityParams.sort_by : undefined
  if (!boost && currentOrderBy.typesensePreset) {
    return { preset: currentOrderBy.typesensePreset }
  }
  // A city `_eval` boost is a coarse tier and leads the sort (matching-city
  // events first, mirroring Algolia optionalFilters). A geo distance boost
  // instead trails the user's explicit sort as a tiebreaker — choosing
  // "newest"/"price" shouldn't be overridden by proximity (that's relevancy's
  // job). See getTypesenseRelevancySortBy for the proximity-first path.
  const isGeoBoost = boost?.startsWith(`${TYPESENSE_GEO_FIELD}(`)
  const sortBy = isGeoBoost
    ? [currentOrderBy.typesenseSortBy, boost].join(',')
    : [boost, currentOrderBy.typesenseSortBy].filter(Boolean).join(',')
  return { sortBy }
}

export type EmptySearchEngineRecovery = 'none' | 'reset_filters' | 'geo_retry'

export const classifyEmptySearchEngineRecovery = (input: {
  hits: unknown[] | undefined
  filtersMatchDefault: boolean
}): EmptySearchEngineRecovery => {
  const { hits, filtersMatchDefault } = input
  if (hits === undefined || hits.length > 0) return 'none'
  if (!filtersMatchDefault) return 'reset_filters'
  return 'geo_retry'
}
